import { CooksyLogo } from "@/components/CooksyLogo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Settings, CreditCard, Bell, HelpCircle, ChefHat, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Shimmer } from "@/components/ui/shimmer";
import type { AppRole } from "@/types/roles";
const ProfileLoadingState = () => {
  return <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Shimmer className="w-20 h-20 rounded-full" />
        <div className="space-y-2">
          <Shimmer className="h-7 w-48" />
          <Shimmer className="h-5 w-32" />
        </div>
      </div>
      
      <Shimmer className="h-10 w-full" />

      <Card className="p-4 space-y-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="flex items-center gap-3">
            <Shimmer className="h-5 w-5" />
            <div className="flex-1 space-y-1">
              <Shimmer className="h-5 w-32" />
              <Shimmer className="h-4 w-48" />
            </div>
          </div>)}
      </Card>

      <Shimmer className="h-10 w-full" />
    </div>;
};
const Profile = () => {
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const {
    data: userData,
    isLoading
  } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');
      console.log('Current user ID:', user.id);

      // Get user profile
      const {
        data: profile
      } = await supabase.from('profiles').select('*').eq('id', user.id).single();

      // Get user roles
      const {
        data: roleData
      } = await supabase.from('user_roles').select('role').eq('user_id', user.id).eq('role', 'cook' as AppRole).maybeSingle();

      // Get admin role
      const {
        data: adminRole
      } = await supabase.from('user_roles').select('*').eq('user_id', user.id).eq('role', 'admin').maybeSingle();

      // Get verification status
      const {
        data: verificationData
      } = await supabase.from('cook_verifications').select('*').eq('cook_id', user.id).maybeSingle();
      const result = {
        ...profile,
        email: user.email,
        isChef: !!roleData?.role,
        isAdmin: !!adminRole,
        verificationStatus: verificationData?.status,
        verificationData
      };
      console.log('Final user data:', result);
      return result;
    }
  });
  const handleChefModeToggle = () => {
    if (userData?.isChef) {
      navigate('/seller/profile');
      toast({
        title: "Switched to Chef Mode",
        description: "You are now in Chef Mode",
        duration: 1800
      });
    } else {
      // Handle different verification statuses
      if (!userData?.verificationStatus) {
        // No verification attempt yet
        navigate('/seller/become-cook');
      } else if (userData.verificationStatus === 'pending') {
        // Application is under review
        navigate('/seller/verification/complete');
      } else if (userData.verificationStatus === 'rejected') {
        // Rejected applications can start over
        navigate('/seller/become-cook');
      } else if (userData.verificationStatus === 'in_progress') {
        // Resume incomplete application
        navigate('/seller/verification/documents');
      } else if (userData.verificationStatus === 'approved') {
        // Approved chefs should use "Switch to Chef Mode"
        navigate('/seller/profile');
      }
    }
  };
  const handleAdminModeToggle = () => {
    navigate('/admin/dashboard');
    toast({
      title: "Switched to Admin Mode",
      description: "You are now in Admin Mode",
      duration: 1800
    });
  };
  const getInitials = () => {
    if (!userData) return '';
    return `${userData.first_name?.[0] || ''}${userData.last_name?.[0] || ''}`.toUpperCase() || 'U';
  };
  const getFullName = () => {
    if (!userData) return 'Loading...';
    return `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || 'Unnamed User';
  };
  const getChefButtonText = () => {
    if (userData?.isChef) {
      return "Switch to Chef Mode";
    }
    switch (userData?.verificationStatus) {
      case 'pending':
        return "View Application Status";
      case 'in_progress':
        return "Complete Your Application";
      case 'rejected':
        return "Reapply as Chef";
      default:
        return "Become a Chef";
    }
  };
  return <div className="min-h-screen bg-background pb-16">
      <header className="sticky top-0 z-50 bg-white border-b border-border">
        
      </header>
      
      <main className="container mx-auto px-4 py-6 space-y-6 max-w-2xl">
        {isLoading ? <ProfileLoadingState /> : <>
            <div className="flex items-center gap-4">
              {userData?.image_url ? <div className="w-20 h-20 rounded-full overflow-hidden">
                  <img src={userData.image_url} alt={getFullName()} className="w-full h-full object-cover" />
                </div> : <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-2xl font-semibold text-primary">{getInitials()}</span>
                </div>}
              <div>
                <h1 className="text-2xl font-bold text-foreground">{getFullName()}</h1>
                <p className="text-muted-foreground">{userData?.email}</p>
              </div>
            </div>

            <Button variant="outline" className="w-full flex items-center gap-2" onClick={handleChefModeToggle}>
              <ChefHat className="h-5 w-5" />
              {getChefButtonText()}
            </Button>

            {userData?.isAdmin && <Button variant="outline" className="w-full flex items-center gap-2" onClick={handleAdminModeToggle}>
                <Shield className="h-5 w-5" />
                Switch to Admin Mode
              </Button>}

            <Card className="p-4 space-y-4">
              <div className="flex items-center gap-3 cursor-pointer hover:bg-accent/50 p-3 rounded-lg transition-colors" onClick={() => navigate('/profile/settings')}>
                <Settings className="h-5 w-5 text-primary" />
                <div className="flex-1">
                  <h3 className="font-medium text-black">Account Settings</h3>
                  <p className="text-sm text-black">Update your profile and preferences</p>
                </div>
              </div>

              <div className="flex items-center gap-3 cursor-pointer hover:bg-accent/50 p-3 rounded-lg transition-colors" onClick={() => navigate('/profile/payment')}>
                <CreditCard className="h-5 w-5 text-primary" />
                <div className="flex-1">
                  <h3 className="font-medium text-black">Payment Methods</h3>
                  <p className="text-sm text-black">Manage your payment options</p>
                </div>
              </div>

              <div className="flex items-center gap-3 cursor-pointer hover:bg-accent/50 p-3 rounded-lg transition-colors" onClick={() => navigate('/profile/notifications')}>
                <Bell className="h-5 w-5 text-primary" />
                <div className="flex-1">
                  <h3 className="font-medium text-black">Notifications</h3>
                  <p className="text-sm text-black">Choose what you want to be notified about</p>
                </div>
              </div>

              <div className="flex items-center gap-3 cursor-pointer hover:bg-accent/50 p-3 rounded-lg transition-colors" onClick={() => navigate('/profile/help')}>
                <HelpCircle className="h-5 w-5 text-primary" />
                <div className="flex-1">
                  <h3 className="font-medium text-black">Support & FAQs</h3>
                  <p className="text-sm text-black">Submit a ticket or read frequently asked questions</p>
                </div>
              </div>
            </Card>

            <Button variant="outline" className="w-full md:w-auto" onClick={async () => {
          await supabase.auth.signOut();
          navigate('/auth');
        }}>
              Sign Out
            </Button>
          </>}
      </main>
    </div>;
};
export default Profile;